// Privacy-respecting "what's new" tracker for anonymous (logged-out) users.
//
// What this stores: localStorage entries for which posts the browser has opened:
//   - `postsTracker`: { [postId]: ISO8601-timestamp }
// Legacy `forumsTracker` (forum-level) is still read once and merged into postsTracker
// on read, then removed.
//
// Bounds (privacy + size): same caps as before (200 keys, 90-day retention).

const POSTS_STORAGE_KEY = "postsTracker";
const LEGACY_FORUMS_STORAGE_KEY = "forumsTracker";
const MAX_TRACKED_ITEMS = 200;
const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;
const MAX_ID_LENGTH = 64;

const isBrowserStorageAvailable = () => {
    try {
        return typeof window !== 'undefined' && !!window.localStorage;
    } catch (_e) {
        return false;
    }
};

const readRawPostsTracker = () => {
    if (!isBrowserStorageAvailable()) {
        return {};
    }
    let raw;
    try {
        raw = window.localStorage.getItem(POSTS_STORAGE_KEY);
    } catch (_e) {
        return {};
    }
    if (!raw || typeof raw !== 'string') {
        return {};
    }
    let parsed;
    try {
        parsed = JSON.parse(raw);
    } catch (_e) {
        return {};
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return {};
    }
    const cleaned = {};
    for (const key of Object.keys(parsed)) {
        if (typeof key !== 'string' || key.length === 0 || key.length > MAX_ID_LENGTH) {
            continue;
        }
        const value = parsed[key];
        if (typeof value !== 'string') {
            continue;
        }
        const ts = Date.parse(value);
        if (Number.isNaN(ts)) {
            continue;
        }
        cleaned[key] = new Date(ts).toISOString();
    }
    return cleaned;
};

const pruneTracker = (tracker) => {
    const now = Date.now();
    const fresh = {};
    for (const key of Object.keys(tracker)) {
        const ts = Date.parse(tracker[key]);
        if (!Number.isNaN(ts) && now - ts <= MAX_AGE_MS) {
            fresh[key] = tracker[key];
        }
    }
    const keys = Object.keys(fresh);
    if (keys.length <= MAX_TRACKED_ITEMS) {
        return fresh;
    }
    keys.sort((a, b) => Date.parse(fresh[b]) - Date.parse(fresh[a]));
    const trimmed = {};
    for (let i = 0; i < MAX_TRACKED_ITEMS; i += 1) {
        const key = keys[i];
        trimmed[key] = fresh[key];
    }
    return trimmed;
};

const writePostsTracker = (tracker) => {
    if (!isBrowserStorageAvailable()) {
        return;
    }
    try {
        window.localStorage.setItem(POSTS_STORAGE_KEY, JSON.stringify(tracker));
    } catch (_e) {
        // Quota exceeded, private mode, etc.
    }
};

export const readPostsTracker = () => {
    return pruneTracker(readRawPostsTracker());
};

export const readPostsTrackerString = () => {
    return JSON.stringify(readPostsTracker());
};

export const markPostViewed = (postId) => {
    if (typeof postId !== 'string' || postId.length === 0 || postId.length > MAX_ID_LENGTH) {
        return;
    }
    const tracker = readRawPostsTracker();
    tracker[postId] = new Date().toISOString();
    writePostsTracker(pruneTracker(tracker));
};

/** @deprecated Use markPostViewed when opening a single post. */
export const markForumViewed = (_forumId) => {
    // No-op: forum visits no longer clear post badges.
};

export const readForumsTracker = () => {
    return readPostsTracker();
};

export const readForumsTrackerString = () => {
    return readPostsTrackerString();
};

export const clearForumsTracker = () => {
    if (!isBrowserStorageAvailable()) {
        return;
    }
    try {
        window.localStorage.removeItem(POSTS_STORAGE_KEY);
        window.localStorage.removeItem(LEGACY_FORUMS_STORAGE_KEY);
    } catch (_e) {
        // best-effort
    }
};
